/**
 * POLAR tracking constraint (AutoCAD `POLARMODE` / F10 + `POLARANG`) — pure,
 * THREE-free, store-free geometry leaf. Sibling of {@link ./orthoConstraint}.
 *
 * Where ORTHO is a HARD lock to the nearest of {0°,90°,180°,270°} (the cursor is
 * ALWAYS projected onto an axis), POLAR tracking is a SOFT snap: the rubber-band
 * cursor is pulled onto the nearest polar tracking ray ONLY while the cursor lies
 * within an angular threshold of that ray; outside the threshold the cursor moves
 * freely (no lock). The tracking rays are the multiples of `POLARANG` (default
 * 90°, so bare polar behaves like ortho) PLUS any user-defined additional angles
 * (AutoCAD `POLARADDANG`, gated by the POLARMODE "additional angles" bit).
 *
 * This module owns the AUTHORITATIVE typed polar-tracking model:
 *   - {@link PolarTrackingConfig} — the full typed configuration (increment,
 *     additional-angles list, whether additional angles participate, and the
 *     measurement basis). NOT a boolean flag — the polar-guide overlay and the
 *     drafting-lock gate both project off this one typed source (CLAUDE.md #1:
 *     CAD semantics live in a typed core BEFORE overlay / renderer / UI).
 *   - {@link buildPolarAngleTable} — resolves the config to the concrete list of
 *     tracking angles in degrees (the overlay's `polarAngleTable` delegates here,
 *     keeping a single source of truth — no duplicated multiples loop).
 *   - {@link nearestPolarAngle} — nearest tracking angle within a threshold.
 *   - {@link applyPolarConstraint} — perpendicular projection of the cursor onto
 *     the locked tracking ray (or the free cursor when nothing locks).
 *
 * Angle basis (AutoCAD POLARMODE "measure polar angles" — Absolute vs
 * Relative to last segment; see `dynamicInput/sysvarStore.ts`
 * `POLARMODE_RELATIVE_BASIS_BIT`):
 *   - "absolute"  → tracking rays are fixed to the UCS X axis (0° = +X).
 *   - "relative"  → the whole table is rotated by `referenceAngleDeg` (the
 *     direction of the previous drawn segment, resolved by
 *     `draftSharedHelpers.polarReferenceAngleForDraftSession`). Wired end to
 *     end: `polarDraftLock.ts` reads the live basis + reference angle at
 *     event time and feeds both here; `polarGuide.ts`'s standalone
 *     `polarAngleTable` helper accepts the same two optional params.
 *
 * References:
 *   - Autodesk: POLARANG / POLARMODE / POLARADDANG (System Variables); "Use Polar
 *     Tracking" — "the cursor snaps along temporary alignment paths defined by
 *     polar angles ... relative to the previous point and, in relative mode, the
 *     previous segment".
 *   - ezdxf: `$POLARANG` reference for the increment-angle semantics.
 */

export type PolarAngleBasis = "absolute" | "relative";

export interface PolarPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Typed polar-tracking configuration. The single source both the polar-guide
 * overlay and the drafting-lock gate consume — never a scattered set of booleans.
 */
export interface PolarTrackingConfig {
  /** POLARANG — the polar angle increment in degrees (> 0). AutoCAD default 90. */
  readonly incrementAngleDeg: number;
  /**
   * POLARADDANG — additional user-defined tracking angles in degrees. Each is an
   * absolute angle (before any relative-basis rotation). Empty when unused.
   */
  readonly additionalAnglesDeg: readonly number[];
  /** POLARMODE bit — whether {@link additionalAnglesDeg} participate. */
  readonly useAdditionalAngles: boolean;
  /** POLARMODE bit — measure from the UCS ("absolute") or the last segment. */
  readonly angleBasis: PolarAngleBasis;
}

/**
 * Result of projecting a cursor onto the polar tracking table.
 *
 * `lockedAngleDeg === null` means NO ray was within the threshold — the cursor is
 * returned verbatim (free movement, exactly like a polar-off / no-anchor path).
 */
export interface PolarLockResult {
  readonly x: number;
  readonly y: number;
  readonly lockedAngleDeg: number | null;
  /** Signed distance from the anchor along the locked ray (0 when unlocked at anchor). */
  readonly distance: number;
}

/** AutoCAD POLARANG fresh-install default (behaves like ORTHO). */
export const DEFAULT_POLAR_ANGLE_DEG = 90;

/**
 * Default angular pull threshold in degrees. AutoCAD engages the polar path when
 * the cursor is within a few degrees of a tracking ray (screen-space APBOX in the
 * app; we honour an angular threshold so the pure helper stays headless-testable).
 */
export const DEFAULT_POLAR_THRESHOLD_DEG = 3;

/** Maximum POLARADDANG entries (AutoCAD caps the additional-angle list at 10). */
export const MAX_POLAR_ADD_ANGLES = 10;

/**
 * Hard upper bound on the number of increment-multiple tracking rays.
 *
 * `POLARANG` is validated only as `0 < v ≤ 360` (SETVAR / persisted localStorage),
 * so a pathological but "valid" tiny increment (e.g. `1e-6`) would otherwise make
 * `count = ceil(360 / step)` ~360M and freeze the pointer-move path the instant
 * POLARMODE engages. A POLARANG finer than `360 / MAX` (= 0.25°) has no meaningful
 * angular resolution anyway — the default 3° pull threshold already overlaps such
 * neighbours — so an increment that would exceed this cap collapses to a bounded,
 * evenly spaced full-circle ring instead of looping unboundedly. Every legitimate
 * increment (≥ 0.25°, incl. AutoCAD's finest 5° preset) stays exact / uncapped.
 */
export const MAX_POLAR_ANGLE_TABLE_SIZE = 1440;

/**
 * Normalise an angle in degrees to the half-open range [0, 360).
 *
 * Exported (not module-private) because `polarDraftLock.ts` reuses it to
 * derive the RELATIVE-basis display angle (locked absolute angle minus
 * `referenceAngleDeg`, which — since it comes straight from `Math.atan2` —
 * can itself be negative and needs the same wraparound-safe normalisation
 * this module already applies to every table entry).
 */
export function norm360(deg: number): number {
  const m = deg % 360;
  return m < 0 ? m + 360 : m;
}

/** Coalesce float drift so identical-mod-360 angles collapse to one Set entry. */
function round6(v: number): number {
  return Math.round(v * 1e6) / 1e6;
}

/**
 * Resolve a {@link PolarTrackingConfig} to the concrete, sorted, duplicate-free
 * list of polar tracking angles in degrees ([0, 360)).
 *
 *   - The base rays are the multiples of the increment angle (falling back to
 *     {@link DEFAULT_POLAR_ANGLE_DEG} when the increment is invalid / ≤ 0). The
 *     multiples span the full circle so BOTH directions of every ray are present
 *     (e.g. increment 90 → 0/90/180/270), matching AutoCAD's bidirectional paths.
 *   - When `useAdditionalAngles`, each finite additional angle is folded in.
 *   - In "relative" basis every angle is rotated by `referenceAngleDeg` (the
 *     previous segment direction); "absolute" ignores it.
 *
 * Multiples are counted (`k * step`) rather than accumulated (`a += step`) so a
 * non-divisor increment (e.g. 7°) never drifts past 360 via float error.
 */
export function buildPolarAngleTable(
  config: PolarTrackingConfig,
  referenceAngleDeg = 0,
): number[] {
  let step =
    Number.isFinite(config.incrementAngleDeg) && config.incrementAngleDeg > 0
      ? config.incrementAngleDeg
      : DEFAULT_POLAR_ANGLE_DEG;
  const rot = config.angleBasis === "relative" ? referenceAngleDeg : 0;
  const out = new Set<number>();
  let count = Math.ceil(360 / step - 1e-9);
  // By-construction DoS guard: bound the ray count regardless of source (SETVAR,
  // localStorage, direct config). A tiny-but-valid increment would otherwise loop
  // ~360/step times; clamp to a bounded even ring (see MAX_POLAR_ANGLE_TABLE_SIZE).
  if (count > MAX_POLAR_ANGLE_TABLE_SIZE) {
    count = MAX_POLAR_ANGLE_TABLE_SIZE;
    step = 360 / count;
  }
  for (let k = 0; k < count; k++) {
    out.add(round6(norm360(k * step + rot)));
  }
  if (config.useAdditionalAngles) {
    for (const extra of config.additionalAnglesDeg) {
      if (Number.isFinite(extra)) out.add(round6(norm360(extra + rot)));
    }
  }
  if (out.size === 0) out.add(round6(norm360(rot)));
  return Array.from(out).sort((a, b) => a - b);
}

/**
 * Find the nearest tracking angle to `cursorAngleDeg` within `thresholdDeg`
 * (wraparound-aware), or `null` when none lock. Ties keep the first encountered
 * (the table is sorted ascending). Shared by the polar-guide overlay's
 * `findActiveAngle` and the constraint below.
 */
export function nearestPolarAngle(
  cursorAngleDeg: number,
  table: readonly number[],
  thresholdDeg = DEFAULT_POLAR_THRESHOLD_DEG,
): number | null {
  let best: number | null = null;
  let bestDiff = thresholdDeg;
  const cand = norm360(cursorAngleDeg);
  for (const a of table) {
    const diff1 = Math.abs(a - cand);
    const d = Math.min(diff1, 360 - diff1);
    if (d < bestDiff) {
      bestDiff = d;
      best = a;
    }
  }
  return best;
}

/**
 * Project `cursor` onto the nearest polar tracking ray from `base` when the
 * cursor's direction is within `thresholdDeg` of that ray; otherwise return the
 * cursor unchanged (free movement — `lockedAngleDeg` null).
 *
 * The lock is a PERPENDICULAR projection onto the ray (the foot of the
 * perpendicular from the cursor), so the rubber band tracks the ray while the
 * point stays at the cursor's along-ray distance — AutoCAD polar-tracking parity.
 * A degenerate cursor-at-anchor input never locks (no direction to measure).
 */
export function applyPolarConstraint(
  base: PolarPoint,
  cursor: PolarPoint,
  angleTableDeg: readonly number[],
  thresholdDeg = DEFAULT_POLAR_THRESHOLD_DEG,
): PolarLockResult {
  const dx = cursor.x - base.x;
  const dy = cursor.y - base.y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-9) {
    return { x: cursor.x, y: cursor.y, lockedAngleDeg: null, distance: 0 };
  }
  const cursorAngle = norm360((Math.atan2(dy, dx) * 180) / Math.PI);
  const locked = nearestPolarAngle(cursorAngle, angleTableDeg, thresholdDeg);
  if (locked === null) {
    return { x: cursor.x, y: cursor.y, lockedAngleDeg: null, distance: dist };
  }
  const rad = (locked * Math.PI) / 180;
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  // Perpendicular projection of the cursor onto the tracking ray.
  const proj = dx * ux + dy * uy;
  return {
    x: base.x + proj * ux,
    y: base.y + proj * uy,
    lockedAngleDeg: locked,
    distance: proj,
  };
}
